import React from 'react';
import Bullet from '../bullet';
import { Forms, Loading, SystemText } from 'e4shared';
import { e4Format } from 'helpers/dates';
import { useDispatch, useSelector } from 'react-redux';
import { setAsideFilters, setAsideFilterValue, setPickerFilters, setPickerFilterValue } from 'actions/ui';
import SearchField from 'components/general/searchField';
import BehaviourField from '../behaviour';
import CourseCompletionField from '../courseCompletion';
import { translateCourse } from 'helpers/translations';
import { hasAccelerateOrChallenger } from 'helpers/company';

export const Filter = ({ field, loading, inline, picker }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = React.useState(false);
    const [all, setAll] = React.useState(false);
    const [sortedCourses, setSortedCourses] = React.useState([]);

    const filterFields = useSelector(state => (picker ? state.ui.pickerFilters : state.ui.asideFilters)) || [];
    const { user } = useSelector(state => state.user);
    const { competencies, courses } = useSelector(state => state.dashboard);
    const ratingsRange = useSelector(state => state.user.user.companySettings?.display?.ratingRange || 5);

    const courseStatuses = [
        { id: 'X', name: SystemText('General/Accelerate/NotStarted/Label') },
        { id: 'P', name: SystemText('General/Accelerate/InProgress/Label') },
        { id: 'C', name: SystemText('General/Accelerate/Completed/Label') },
    ];

    if (field.type === 'courseStatus' && !hasAccelerateOrChallenger(user)) {
        return null;
    }

    React.useEffect(() => {
        if (courses) {
            setSortedCourses(
                courses
                    .map(x => translateCourse(user.language_Code, x))
                    .map(x => ({ ...x, id: x.course_Id, name: x.title }))
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
            );
        }
    }, [courses]);

    React.useEffect(() => {
        const thisField = filterFields.find(x => x.property === field.property);
        if (typeof thisField?.value === 'object' && !thisField?.value) {
            setAll(false);
        }
    }, [filterFields]);

    const toggle = () => setOpen(!open);

    const onChange = (field, value) => {
        const payload = { property: field.property, value };
        if (picker) {
            dispatch(setPickerFilterValue(payload));
        } else {
            dispatch(setAsideFilterValue(payload));
        }
    };

    const dateField = field => {
        return (
            <div className="flex flex-center">
                <Forms.Date2
                    data-testid={`filter-${field.property}`}
                    value={field.value}
                    onChange={x => onChange(field, x)}
                    allowNull
                />
            </div>
        );
    };

    const fromText = SystemText('General/Controls/Filters/Range/From');
    const toText = SystemText('General/Controls/Filters/Range/To');
    const allText = SystemText('General/Controls/Filters/Course/All');

    const rangeValue = x => {
        const currentValue = x || '|';
        const valueArray = currentValue.split('|');

        if (!valueArray[0] && !valueArray[1]) {
            return '';
        }

        if (!valueArray[1]) {
            return `${fromText} ${e4Format(valueArray[0])}`;
        }

        if (!valueArray[0]) {
            return `${toText} ${e4Format(valueArray[1])}`;
        }

        return `${e4Format(valueArray[0])} - ${e4Format(valueArray[1])}`;
    };

    const rangeField = field => {
        if (!field.name) {
            return null;
        }

        const currentValue = field.value || '|';
        const valueArray = currentValue.split('|');

        const onRangeChange = (fromTo, x) => {
            if (fromTo === 'from') {
                valueArray[0] = x;
            } else {
                valueArray[1] = x;
            }

            const newValue = valueArray.join('|');

            onChange(field, newValue);
        };

        return (
            <div className="flex flex-top">
                <Forms.Date2
                    label={fromText}
                    data-testid={`filter-${field.property}-from`}
                    value={valueArray[0]}
                    onChange={x => onRangeChange('from', x)}
                    allowNull
                />
                <span className="ml10">
                    <Forms.Date2
                        label={toText}
                        data-testid={`filter-${field.property}-to`}
                        value={valueArray[1]}
                        onChange={x => onRangeChange('to', x)}
                        allowNull
                    />
                </span>
            </div>
        );
    };

    const textField = field => {
        return (
            <div>
                <SearchField
                    data-testid={`filter-${field.property}`}
                    value={field.value}
                    onChange={x => onChange(field, x)}
                />
            </div>
        );
    };

    const listField = field => {
        const triggerCheck = e => {
            const id = field.stringValues ? e.target.value : parseInt(e.target.value);
            const temp = [...(field.value || [])];
            if (e.target.checked) {
                setAll(field.data?.length === temp.length + 1);
                onChange(field, temp.concat([id]));
            } else {
                setAll(false);
                onChange(
                    field,
                    temp.filter(x => x !== id)
                );
            }
        };

        const triggerSelectAll = e => {
            if (e.target.checked) {
                onChange(
                    field,
                    field.data.map(d => d.id)
                );
            }
        };

        const triggerDeselectAll = () => {
            setAll(false);
            setTimeout(() => onChange(field, []), 100);
        };

        return (
            <>
                {field.data?.length === field.value?.length ? (
                    <label className="flex flex-center">
                        <button
                            data-testid={`filter-${field.property}-unselect-all`}
                            className="btn btn-link"
                            onClick={triggerDeselectAll}
                        >
                            {SystemText('General/Controls/Filters/Deselect')}
                        </button>
                    </label>
                ) : (
                    <label className="flex flex-center">
                        <input
                            data-testid={`filter-${field.property}-all`}
                            type="checkbox"
                            checked={all}
                            value={field.property}
                            onChange={triggerSelectAll}
                            className=""
                        />
                        <span className="flex-grow ml5">{SystemText('General/Controls/Filters/Select')}</span>
                    </label>
                )}
                <div className="list-field">
                    {loading ? (
                        <Loading />
                    ) : (
                        field.data.map(d => (
                            <label key={d.id} className="flex flex-center">
                                <input
                                    data-testid={`filter-${field.property}-${d.id}`}
                                    type="checkbox"
                                    checked={field.value?.some(x => x === d.id)}
                                    value={d.id}
                                    onChange={triggerCheck}
                                    className=""
                                />
                                <span className="flex-grow ml5">{d[field.nameProperty || 'name']}</span>
                            </label>
                        ))
                    )}
                </div>
            </>
        );
    };

    const courseCompletionField = field => {
        return (
            <CourseCompletionField
                field={field}
                onChange={x => onChange(field, x)}
                sortedCourses={sortedCourses}
                statuses={courseStatuses}
            />
        );
    };

    const behaviourField = field => {
        return <BehaviourField field={field} onChange={x => onChange(field, x)} />;
    };

    const clearFilter = (property, value) => {
        const temp = [...filterFields];
        const thisFilter = temp.find(x => x.property === property);
        if (thisFilter.type === 'list') {
            thisFilter.value = (thisFilter.value || []).filter(x => x !== value);
        } else if (thisFilter.type === 'courseStatus') {
            thisFilter.value = (thisFilter.value || []).filter(x => x.id !== value);
            thisFilter.value = thisFilter.value?.length ? thisFilter.value : [{ id: null, status: [] }];
        } else if (thisFilter.type === 'behaviourScore') {
            thisFilter.value = (thisFilter.value || []).filter(x => x.id !== value);
            thisFilter.value = thisFilter.value?.length ? thisFilter.value : [{ id: null, range: [1, ratingsRange] }];
        } else if (thisFilter.type === 'text') {
            thisFilter.value = '';
        } else {
            thisFilter.value = null;
        }

        if (picker) {
            dispatch(setPickerFilters(temp));
        } else {
            dispatch(setAsideFilters(temp));
        }
    };

    const hasValue = field => {
        if (field.type === 'behaviourScore' || field.type === 'courseStatus') {
            return field.value?.some(x => x.id);
        }

        return field.value?.length > 0;
    };

    const mapStatus = status => {
        if (status?.length === 0 || status?.length === 3) {
            return allText;
        }

        if (status?.length === 1) {
            return courseStatuses.find(x => x.id === status[0]).name;
        }

        return `${courseStatuses.find(x => x.id === status[0]).name}, ${courseStatuses.find(x => x.id === status[1]).name}`;
    };

    return (
        <div className="filter-item">
            <button
                className="btn-link btn-block flex flex-center"
                data-testid={`toggle-filter-${field.property}`}
                onClick={toggle}
            >
                <span className="flex-grow">{field.name}</span>
                <i className={`fa expander fa-chevron-${open ? 'up' : 'down'}`} />
            </button>
            {hasValue(field) && (
                <div className="flex flex-center mt10 filter-bullet-holder">
                    {field.type === 'date' && (
                        <Bullet
                            property={field.property}
                            value={field.value}
                            label={e4Format(field.value)}
                            onClick={inline ? clearFilter : null}
                        />
                    )}
                    {field.type === 'text' && (
                        <Bullet property={field.property} value={field.value} onClick={inline ? clearFilter : null} />
                    )}
                    {field.type === 'range' && (
                        <Bullet
                            property={field.property}
                            value={field.value}
                            label={rangeValue(field.value)}
                            onClick={inline ? clearFilter : null}
                        />
                    )}
                    {field.type === 'list' && !inline && (
                        <>
                            {field.value.length >= 1 && (
                                <Bullet
                                    property={field.property}
                                    value={field.value[0]}
                                    label={field.data.find(d => d.id === field.value[0])[field.nameProperty || 'name']}
                                    onClick={inline ? clearFilter : null}
                                />
                            )}
                            {field.value.length >= 2 && (
                                <Bullet
                                    property={field.property}
                                    value={field.value[1]}
                                    label={field.data.find(d => d.id === field.value[1])[field.nameProperty || 'name']}
                                    onClick={inline ? clearFilter : null}
                                />
                            )}
                            {field.value.length >= 3 && (
                                <Bullet
                                    property={field.property}
                                    extra={field.value.length - 2}
                                    onClick={inline ? clearFilter : null}
                                />
                            )}
                        </>
                    )}
                    {field.type === 'list' &&
                        inline &&
                        field.value.map(x => {
                            return (
                                <Bullet
                                    key={`${field.property}-${x}`}
                                    property={field.property}
                                    value={x}
                                    label={field.data.find(d => d.id === x)[field.nameProperty || 'name']}
                                    onClick={inline ? clearFilter : null}
                                />
                            );
                        })}
                    {field.type === 'behaviourScore' &&
                        inline &&
                        field.value.map(x => {
                            return (
                                <Bullet
                                    key={`${field.property}-${x.id}`}
                                    property={field.property}
                                    value={x.id}
                                    label={`${competencies.find(c => c.id === x.id)?.name || 'ERROR'} (${x.range[0]} - ${
                                        x.range[1]
                                    })`}
                                    onClick={inline ? clearFilter : null}
                                />
                            );
                        })}
                    {field.type === 'courseStatus' &&
                        inline &&
                        field.value.map(x => {
                            return (
                                <Bullet
                                    key={`${field.property}-${x.id}`}
                                    property={field.property}
                                    value={x.id}
                                    label={`${sortedCourses.find(c => c.id === x.id)?.name || 'ERROR'} (${mapStatus(
                                        x.status
                                    )})`}
                                    onClick={inline ? clearFilter : null}
                                />
                            );
                        })}
                </div>
            )}
            {open && (
                <div className="mt10">
                    {field.type === 'date' && dateField(field)}
                    {field.type === 'range' && rangeField(field)}
                    {field.type === 'text' && textField(field)}
                    {field.type === 'list' && listField(field)}
                    {field.type === 'courseStatus' && courseCompletionField(field)}
                    {field.type === 'behaviourScore' && behaviourField(field)}
                </div>
            )}
        </div>
    );
};

export default Filter;
